import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveBackendMessage } from '../Utils/apiMessages';

export const useErrorHandler = () => {
  const { t } = useTranslation();

  const handleError = useCallback((error) => {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error details:', error);
    }

    const responseData = error.response?.data;

    // Handle API errors with structured payload
    if (responseData) {
      const {
        errors,
        message: errorMessage,
        message_code: messageCode,
        message_data: messageData,
        metadata,
      } = responseData;

      // Field-level validation errors
      if (errors?.length) {
        errors.forEach(({ field, description }) => {
          message.error(
            t('field_error', {
              field,
              description,
              defaultValue: `${field}: ${description}`,
            }),
          );
        });
        return;
      }

      // Prefer localized message_code + dynamic parameters when available
      if (messageCode) {
        const resolved = resolveBackendMessage(t, {
          message_code: messageCode,
          message: errorMessage,
          message_data: messageData || metadata,
        });

        if (resolved) {
          message.error(resolved);
          return;
        }
      }

      // Fallback to raw backend message
      if (errorMessage) {
        message.error(errorMessage);
        return;
      }
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      message.error(
        t('network_error_message', {
          defaultValue: 'Network error. Please check your connection and try again.',
        }),
      );
      return;
    }

    // Handle unexpected errors
    if (!responseData) {
      message.error(
        t('unexpected_error_occurred', {
          defaultValue: 'An unexpected error occurred',
        }),
      );
    }
  }, [t]);

  return { handleError };
};
